import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, Filter, Download } from "lucide-react";
import { toast } from "sonner";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}
function formatDateIT(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function StatusBadge({ stato }) {
  const map = {
    aperto: { label: "Aperto", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    chiuso_auto: { label: "Chiuso auto", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    chiuso_manuale: { label: "Chiuso", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    riaperto: { label: "Riaperto", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  };
  const it = map[stato] || { label: stato, cls: "bg-slate-100" };
  return <Badge variant="outline" className={`${it.cls} rounded-full px-2.5 py-0.5 text-xs`}>{it.label}</Badge>;
}

export default function CollaboratorStorico() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    data_from: "",
    data_to: "",
    only_urgent: false,
    solo_non_versati: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false || v == null) return;
      params[k] = v;
    });
    try {
      const r = await api.get("/cash-sheets", { params });
      setRows(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const downloadFile = async (sheetId, dataSheet, kind) => {
    try {
      const res = await api.get(`/cash-sheets/${sheetId}/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `foglio_cassa_${dataSheet}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="collab-storico-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Archivio</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">I miei fogli cassa</h1>
        <p className="text-sm text-slate-500 mt-1">
          Consulta lo storico dei tuoi fogli cassa e scarica i report PDF/Excel.
        </p>
      </div>

      <Card className="p-4 border-slate-200">
        <div className="flex items-center gap-2 mb-3 text-slate-700"><Filter className="w-4 h-4" /> <span className="text-sm font-semibold">Filtri</span></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label>Dal</Label>
            <Input type="date" value={filters.data_from} onChange={(e) => setFilters({ ...filters, data_from: e.target.value })} data-testid="filter-from" />
          </div>
          <div>
            <Label>Al</Label>
            <Input type="date" value={filters.data_to} onChange={(e) => setFilters({ ...filters, data_to: e.target.value })} data-testid="filter-to" />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={filters.only_urgent} onCheckedChange={(v) => setFilters({ ...filters, only_urgent: !!v })} data-testid="filter-urgent" />
              Solo con righe urgenti
            </label>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={filters.solo_non_versati} onCheckedChange={(v) => setFilters({ ...filters, solo_non_versati: !!v })} data-testid="filter-non-versati" />
              Solo con clienti non versati
            </label>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Data</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Stato</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Righe</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Urgenti</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Totale</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Da versare</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={7} className="text-center py-8 text-slate-500">Caricamento…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500" data-testid="empty-storico">Nessun foglio cassa.</td></tr>}
              {rows.map((r, idx) => (
                <tr key={r.sheet.id} className="hover:bg-slate-50" data-testid={`storico-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{formatDateIT(r.sheet.data_foglio_cassa)}</td>
                  <td className="px-4 py-3"><StatusBadge stato={r.sheet.stato} /></td>
                  <td className="px-4 py-3 text-right font-mono-num text-slate-700">{r.totals.count_totale}</td>
                  <td className="px-4 py-3 text-right font-mono-num">
                    {r.totals.count_urgenti > 0 ? <span className="text-red-600 font-semibold">{r.totals.count_urgenti}</span> : <span className="text-slate-400">0</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold text-slate-900">{formatEUR(r.totals.totale_generale)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">
                    {(r.totals.totale_non_versato || 0) > 0
                      ? (
                        <div className="flex flex-col items-end">
                          <span className="text-amber-700 font-semibold">{formatEUR(r.totals.totale_non_versato)}</span>
                          {r.totals.versamento_mode === "polizze" && (r.totals.titoli_non_versati || 0) > 0 && (
                            <span className="text-[10px] text-amber-700/80">
                              {r.totals.titoli_non_versati} titol{r.totals.titoli_non_versati === 1 ? "o" : "i"}
                            </span>
                          )}
                        </div>
                      )
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={`/foglio-cassa/${r.sheet.id}`}>
                        <Button size="sm" variant="outline" data-testid={`view-${idx}`}><Eye className="w-3.5 h-3.5" /></Button>
                      </Link>
                      <Button size="sm" variant="outline" onClick={() => downloadFile(r.sheet.id, r.sheet.data_foglio_cassa, "pdf")} data-testid={`pdf-${idx}`}><Download className="w-3.5 h-3.5" /> PDF</Button>
                      <Button size="sm" variant="outline" onClick={() => downloadFile(r.sheet.id, r.sheet.data_foglio_cassa, "excel")} data-testid={`xls-${idx}`}><Download className="w-3.5 h-3.5" /> XLS</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
